import { t } from 'i18next';

import { AdminHealthOverview } from '@activepieces/shared';

import { BACKLOG_AGE_ALERT_SECONDS } from './constants';
import {
  longestRunningQueue,
  oldestWaitingQueue,
  stuckActiveJobThresholdSeconds,
} from './queue-ages';

// One definition of "red", read by the Health tiles and by the Overview status
// line, so the two pages can never disagree on whether something is wrong.
export const isWorkerless = (health: AdminHealthOverview): boolean =>
  health.machines.length === 0;

export const isBacklogTooOld = (health: AdminHealthOverview): boolean =>
  (oldestWaitingQueue(health.queues)?.oldestJobAgeSeconds ?? 0) >
  BACKLOG_AGE_ALERT_SECONDS;

export const hasStuckActiveJob = (health: AdminHealthOverview): boolean =>
  (longestRunningQueue(health.queues)?.oldestActiveJobAgeSeconds ?? 0) >
  stuckActiveJobThresholdSeconds(health.runtimeConfig);

export const hasOrphanRuns = (health: AdminHealthOverview): boolean =>
  (health.orphanRuns?.total ?? 0) > 0;

const HEALTH_ALERTS = [
  {
    isRaised: (health: AdminHealthOverview) =>
      health.services.some((service) => service.status === 'failed'),
    label: () => t('a dependency is not responding'),
  },
  { isRaised: isWorkerless, label: () => t('no worker online') },
  { isRaised: isBacklogTooOld, label: () => t('queue backlog') },
  { isRaised: hasStuckActiveJob, label: () => t('stuck job') },
  { isRaised: hasOrphanRuns, label: () => t('orphan runs') },
  {
    isRaised: (health: AdminHealthOverview) =>
      health.systemJobs.some((job) => job.isOverdue),
    label: () => t('scheduler stalled'),
  },
];

export const raisedHealthAlerts = (health: AdminHealthOverview): string[] =>
  HEALTH_ALERTS.filter((alert) => alert.isRaised(health)).map((alert) =>
    alert.label(),
  );
